import { Board } from '@/api';
import { CycleBoard } from '@/types/board-types';
import { DateTimeRange } from '@/models/common.models';
import { DSCubeQuery } from '@/models/cube.models';
import {
  CreateExportItemsJob,
  ExportJob,
  ExportJobListResponse,
  ExportJobResponse,
  FindExportJobsQueryParams,
} from '@/models/data.models';
import { StoredQueryOptions } from '@/models/item.models';
import { BaseModule } from '@/modules/common.module';
import {
  churnAssignedUnassignedQuery,
  churnCreatedDeletedQuery,
  churnCycleQuery,
  churnPlannedQuery,
  completedOverviewLaneQuery,
  completedOverviewQuery,
  customCycleTimeQuery,
  customCycleTimeSumsQuery,
  cycleTimeQuery,
  cycleVelocityQuery,
  itemTransactionQuery,
  leadTimeQuery,
  throughputQuery,
  trendingAssignedQuery,
  trendingCompletedQuery,
  trendingLaneQuery,
  trendingPlannedQuery,
  trendingPointEstimatedQuery,
  trendingTimeEstimateQuery,
  userCompletedTimeItemTimeSpentTotalQuery,
  userCompletedTimeSubItemTimeSpentTotalQuery,
  allUsersTimeEntriesTotalQuery,
  allUsersSubItemTimeSpentTotalQuery,
} from '@/utils/queries/cube';
import cubejs, { CubejsApi, Query } from '@cubejs-client/core';

export class DataModule extends BaseModule {
  constructor(organizationId: string) {
    super(`v1/organizations/${organizationId}/analytics`);
  }

  async getCubeAuth(): Promise<string> {
    const response = await this.get<{ data: { token: string } }>(
      `cube-auth`,
      {}
    );
    return response.data.data.token;
  }

  async findExportJobs(
    query: FindExportJobsQueryParams
  ): Promise<ExportJobListResponse> {
    const response = await this.get<ExportJobListResponse>(`exports`, {
      params: query,
    });
    return response.data;
  }

  async exportItems(data: CreateExportItemsJob): Promise<ExportJobResponse> {
    const response = await this.post<ExportJobResponse>(`exports/items`, data);
    return response.data;
  }

  async getReportByExportJobId(exportJobId: string): Promise<{ url: string }> {
    const response = await this.get<{ url: string }>(
      `exports/items/${exportJobId}/download`
    );
    return response.data;
  }

  async getExportJobById(exportJobId: string): Promise<ExportJob> {
    const response = await this.get<ExportJobResponse>(
      `exports/items/${exportJobId}`
    );
    return response.data.data;
  }

  async deleteExportByJobById(exportJobId: string): Promise<ExportJob> {
    const response = await this.delete<ExportJobResponse>(
      `exports/items/${exportJobId}`
    );
    return response.data.data;
  }
}

export class CubeDataModule {
  public cubejs: CubejsApi;

  constructor(private readonly organizationId: string, token: string) {
    this.cubejs = cubejs(token, {
      apiUrl: import.meta.env.VITE_CUBE_API_ENDPOINT || '',
    });
  }

  async getCurrentProgressData(filters?: StoredQueryOptions) {
    const [completedOverviewResponse, completedOverviewLaneResponse] =
      await Promise.all([
        this.cubejs.load({
          ...completedOverviewQuery(this.organizationId, filters),
        }),

        this.cubejs.load({
          ...completedOverviewLaneQuery(this.organizationId, filters),
        }),
      ]);

    return {
      completedOverview: completedOverviewResponse.rawData()[0],
      completedOverviewLane: completedOverviewLaneResponse.rawData(),
    };
  }

  async getTrendingCompletedData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...trendingCompletedQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    });
    return response.rawData();
  }

  async getTrendingTimeEstimatedData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...trendingTimeEstimateQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    });
    return response.rawData();
  }

  async getTrendingPointEstimatedData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...trendingPointEstimatedQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    });
    return response.rawData();
  }

  async getTrendingAssignedData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...trendingAssignedQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    });
    return response.rawData();
  }

  async getTrendingPlannedData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...trendingPlannedQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    });
    return response.rawData();
  }

  async getTrendingLaneData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...trendingLaneQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    });
    return response.rawData();
  }

  async getThroughputData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...throughputQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    });
    return response.rawData();
  }

  async getCumulativeFlowData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...trendingLaneQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    });
    return response.rawData();
  }

  async getItemTransactionData(
    dateRange?: DateTimeRange,
    filters?: StoredQueryOptions,
    paginationOptions?: { limit: number; offset: number }
  ) {
    const response = await this.cubejs.load({
      ...itemTransactionQuery(this.organizationId, dateRange, filters),
      ...(paginationOptions || {}),
    });
    return response.rawData();
  }

  async getItemsData(
    query: DSCubeQuery,
    paginationOptions?: { limit: number; offset: number }
  ) {
    const response = await this.cubejs.load({
      ...query,
      ...(paginationOptions || {}),
    });

    return response.rawData();
  }

  async getChurnCycleData(cycles: CycleBoard[], filters?: StoredQueryOptions) {
    const response = await this.cubejs.load({
      ...churnCycleQuery(this.organizationId, cycles, filters),
    } as Query);
    return response.rawData();
  }

  async getChurnCreatedDeletedData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...churnCreatedDeletedQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    } as Query);
    return response.rawData();
  }

  async getChurnAssignedUnassignedData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...churnAssignedUnassignedQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    } as Query);
    return response.rawData();
  }

  async getChurnPlannedUnplannedData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...churnPlannedQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    } as Query);
    return response.rawData();
  }

  async getCycleVelocityData(cycles: Board[], filters?: StoredQueryOptions) {
    const response = await this.cubejs.load({
      ...cycleVelocityQuery(this.organizationId, cycles, filters),
    } as Query);
    return response.rawData();
  }

  async getLeadTimeData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...leadTimeQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    } as Query);
    return response.rawData();
  }

  async getCycleTimeData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...cycleTimeQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        filters
      ),
    } as Query);
    return response.rawData();
  }

  async getCustomCycleTimeData(
    dateRange: DateTimeRange,
    dateGranularity: 'day' | 'week' | 'month',
    fromLaneId: string,
    toLaneId: string,
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...customCycleTimeQuery(
        this.organizationId,
        dateRange,
        dateGranularity,
        fromLaneId,
        toLaneId,
        filters
      ),
    } as Query);
    return response.rawData();
  }

  async getCustomCycleTimeSumsData(
    dateRange: DateTimeRange,
    fromLaneId: string,
    toLaneId: string,
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...customCycleTimeSumsQuery(
        this.organizationId,
        dateRange,
        fromLaneId,
        toLaneId,
        filters
      ),
    } as Query);
    return response.rawData();
  }

  async getUserCompletedTimeItemTimeSpentTotalData(
    assigneeUsername: string,
    dateRange: DateTimeRange,
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...userCompletedTimeItemTimeSpentTotalQuery(
        this.organizationId,
        assigneeUsername,
        dateRange,
        filters
      ),
    } as Query);
    return response.rawData();
  }

  async getUserCompletedTimeSubItemTimeSpentTotalData(
    assigneeUsername: string,
    dateRange: DateTimeRange,
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...userCompletedTimeSubItemTimeSpentTotalQuery(
        this.organizationId,
        assigneeUsername,
        dateRange,
        filters
      ),
    } as Query);
    return response.rawData();
  }

  async getAllUsersTimeEntriesTotalData(
    dateRange: DateTimeRange,
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...allUsersTimeEntriesTotalQuery(dateRange, filters),
    } as Query);
    return response.rawData();
  }

  async getAllUsersSubItemTimeSpentTotalData(
    dateRange: DateTimeRange,
    filters?: StoredQueryOptions
  ) {
    const response = await this.cubejs.load({
      ...allUsersSubItemTimeSpentTotalQuery(dateRange, filters),
    } as Query);
    return response.rawData();
  }
}
